import {
  ArrayInput,
  FormDataConsumer,
  NumberInput,
  ReferenceInput,
  SelectInput,
  SimpleFormIterator,
  useTranslate,
  useSimpleFormIteratorItem,
  required,
} from "react-admin";
import { Grid, Tooltip, IconButton, Typography } from "@mui/material";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import React, { useEffect, useState } from "react";
import { settings } from "app";
import dataProvider from "server/dataProvider";
import { administrationRequests } from "api/administrationApi";

const CustomRemoveButton = ({ record, handleRemoveSpeciality }) => {
  const translate = useTranslate();
  const { remove } = useSimpleFormIteratorItem();

  const handleClick = () => {
    if (record.specialityId) {
      handleRemoveSpeciality(record.specialityId);
    }
    remove();
  };

  const title = translate("ra.action.remove");
  return (
    <Tooltip title={title}>
      <IconButton
        color="warning"
        onClick={handleClick}
      >
        <RemoveCircleOutlineIcon />
      </IconButton>
    </Tooltip>
  );
};

const SpecialitiesList = ({
  directionId,
  excludedSpecialityIds,
  campaignIsStarted,
  handleSpecialityChange,
  handleRemoveSpeciality,
  specialityShowGender,
  hasAvaliablePaidEducationCapacity,
  loading,
  isClone = false,
  showZvoCapacity = true,
}) => {
  const [hasSeparatePaidCampaign, setHasSeparatePaidCampaign] = useState(true);

  useEffect(() => {
    const fetchDirectionFlags = async () => {
      try {
        const { data } = await dataProvider.customRequest(administrationRequests.getDirectionFlags(directionId));
        setHasSeparatePaidCampaign(data?.hasSeparatePaidCampaign);
      } catch (error) {
        console.error("Неуспешно зареждане на флаговете за направление", error);
        setHasSeparatePaidCampaign(true);
      }
    };

    if (directionId && hasAvaliablePaidEducationCapacity) {
      fetchDirectionFlags();
    }
  }, [directionId, hasAvaliablePaidEducationCapacity]);

  const greaterThanZero = value => {
    if (value === undefined || value === null) {
      return;
    }

    if (value <= 0) {
      return "Стойността трябва да е по-голяма от 0!";
    }
  };

  const validateCandidatesCapacity = scopedFormData => {
    const zvoCapacity = scopedFormData?.capacityDto?.zvoCapacity;
    const maleCapacity = scopedFormData?.capacityDto?.maleCapacity;
    const femaleCapacity = scopedFormData?.capacityDto?.femaleCapacity;

    if (!zvoCapacity || !maleCapacity || !femaleCapacity) {
      return;
    }

    const candidatesCapacity = scopedFormData?.capacityDto?.candidatesCapacity;
    if (femaleCapacity + maleCapacity + zvoCapacity !== candidatesCapacity) {
      return "Общият капацитет за мъже, жени и ЗВО трябва да е равен на общия капацитет за кандидати";
    }
  };

  return (
    <>
      <Grid
        item
        xs={12}
        md={12}
      >
        <Typography color="primary">Специалности</Typography>
      </Grid>
      <Grid
        item
        xs={12}
        md={12}
      >
        <ArrayInput
          source="specialityDtos"
          label=" "
          defaultValue={[{}]}
        >
          <SimpleFormIterator
            disableReordering
            disableRemove
            disableAdd={campaignIsStarted && !isClone}
            inline
            sx={settings.style.iteratorWithoutGap}
          >
            <FormDataConsumer>
              {({ scopedFormData, formData }) => {
                const isGenderDistribution = specialityShowGender
                  ? specialityShowGender[scopedFormData?.specialityId]
                  : null;
                return (
                  <Grid
                    container
                    spacing={2}
                  >
                    <Grid
                      item
                      xs={12}
                      md={4}
                    >
                      <ReferenceInput
                        source="specialityId"
                        reference={administrationRequests.getSpecialitiesSelect()}
                        filter={{
                          excludeIds: excludedSpecialityIds,
                          directionId: directionId,
                          filterByUser: true,
                        }}
                      >
                        <SelectInput
                          label="Специалност"
                          onChange={event => handleSpecialityChange(event, formData.specialityDtos)}
                          validate={required()}
                          readOnly={!isClone && (campaignIsStarted || loading)}
                        />
                      </ReferenceInput>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      md={2}
                    >
                      <NumberInput
                        label="Брой места"
                        source="capacityDto.candidatesCapacity"
                        validate={[required(), greaterThanZero]}
                        readOnly={loading}
                      />
                    </Grid>
                    {(scopedFormData?.isDistributedByGender || isGenderDistribution) && (
                      <>
                        <Grid
                          item
                          xs={12}
                          md={2}
                        >
                          <NumberInput
                            label="Брой места за мъже"
                            source="capacityDto.maleCapacity"
                            validate={[greaterThanZero, () => validateCandidatesCapacity(scopedFormData)]}
                            readOnly={loading}
                          />
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          md={2}
                        >
                          <NumberInput
                            label="Брой места за жени"
                            source="capacityDto.femaleCapacity"
                            validate={[greaterThanZero, () => validateCandidatesCapacity(scopedFormData)]}
                            readOnly={loading}
                          />
                        </Grid>
                      </>
                    )}
                    {showZvoCapacity && (
                      <Grid
                        item
                        xs={12}
                        md={3}
                      >
                        <NumberInput
                          label="Брой места за ЗВО"
                          source="capacityDto.zvoCapacity"
                          validate={[greaterThanZero, () => validateCandidatesCapacity(scopedFormData)]}
                          readOnly={loading}
                        />
                      </Grid>
                    )}
                    {hasSeparatePaidCampaign === false ? (
                      <Grid
                        item
                        xs={12}
                        md={2}
                      >
                        <NumberInput
                          label="Брой места за платено обучение"
                          source="capacityDto.paidEducationCapacity"
                          validate={[greaterThanZero]}
                          readOnly={loading}
                        />
                      </Grid>
                    ) : null}
                    {!campaignIsStarted && (
                      <Grid
                        item
                        xs={12}
                        md={1}
                      >
                        <CustomRemoveButton
                          record={scopedFormData}
                          handleRemoveSpeciality={handleRemoveSpeciality}
                        />
                      </Grid>
                    )}
                  </Grid>
                );
              }}
            </FormDataConsumer>
          </SimpleFormIterator>
        </ArrayInput>
      </Grid>
    </>
  );
};

export default SpecialitiesList;
